import { useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setUserMessage } from "../../../system/reducers/userMessage/actions";
import moment from "moment";
import fileDownload from "js-file-download";
import { Add, Description, GetApp, Upload } from "@mui/icons-material";
import { Box, Chip, CircularProgress, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import Layout from "../../../Components/Layout/Layout";
import CompaniesNavigation from "Navigation/CompaniesNavigation";
import PaperTable from "../../../Components/PaperTable";
import Sidebar from "Components/Sidebar/Sidebar";
import SidebarGroup from "Components/Sidebar/SidebarGroup";
import SidebarButton from "Components/Sidebar/SidebarButton";
import showMessage from "Dialogs/showMessage";
import useAsyncEffect from "../../../system/useAsyncEffect";
import useCompanies from "../../../system/useCompanies";
import parseContentDispositionFilename from "system/parseContentDispositionFilename";
import dateFormats from "system/dateFormats";
import { TransportPriceList } from "../../../system/Domain";
import { axleLabels, getListStatus, statusLabels } from "./transportPriceLabels";

const downloadFile = async (url: string, params: object) => {
	const response = await axios.get(url, { responseType: "blob", params });
	fileDownload(response.data, parseContentDispositionFilename(response.headers["content-disposition"]));
};

const statusColors = { planned: "info", active: "success", expired: "default" } as const;

const TransportPriceCarrierPage = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const { carrierId } = useParams<{ carrierId: string }>();
	const [companies] = useCompanies();
	const [lists, setLists] = useState<TransportPriceList[]>();
	const [reloadCounter, setReloadCounter] = useState(0);
	const fileInput = useRef<HTMLInputElement>(null);

	const carrier = companies?.find(c => c.id === Number(carrierId));
	const today = moment().format("YYYY-MM-DD");
	const basePath = `/companies/transport-prices/${carrierId}`;

	useAsyncEffect(async (cancelToken) => {
		const { data } = await axios.get<TransportPriceList[]>("/api/transport-prices/lists", {
			params: { carrierId },
			cancelToken: cancelToken.getAxiosToken()
		});

		setLists(data);
	}, [carrierId, reloadCounter]);

	const hasActive = useMemo(() => lists?.some(l => getListStatus(l, today) === "active"), [lists, today]);

	const upload = async (file: File) => {
		const body = new FormData();
		body.append("file", file);

		try {
			// 400 carries the cell errors and is handled here instead of by the global error toast
			const { data, status } = await axios.post<{ lists: number; routes: number; prices: number; errors?: string[] }>(
				"/api/transport-prices/import", body, { params: { carrierId }, validateStatus: s => (s >= 200 && s < 300) || s === 400 }
			);

			if (status === 400) {
				throw { response: { data } };
			}

			dispatch(setUserMessage(`Upload erfolgreich: ${data.lists} Preislisten mit ${data.routes} Strecken und ${data.prices} Preisen wurden angelegt.`, "success"));
			setReloadCounter(c => c + 1);
		} catch (error: any) {
			const errors: string[] | undefined = error?.response?.data?.errors;
			if (!errors) {
				if (error?.response?.status) {
					throw error;
				}

				await showMessage({ title: "Upload abgelehnt", color: "error", message: "Die Datei wurde abgelehnt, es wurde nichts gespeichert." });
				return;
			}

			await showMessage({
				title: "Upload abgelehnt, es wurde nichts gespeichert",
				color: "error",
				maxWidth: "md",
				message: (
					<>
						{errors.slice(0, 30).map((e, i) => <Typography key={i}>{e}</Typography>)}
						{errors.length > 30 && <Typography>… und {errors.length - 30} weitere Fehler</Typography>}
					</>
				)
			});
		}
	};

	return (
		<Layout
			title={carrier ? `Frachtpreise – ${carrier.name}` : "Frachtpreise"}
			search="disabled"
			navigation={<CompaniesNavigation />}
			sidebar={(
				<Sidebar>
					<SidebarGroup>
						<SidebarButton
							color="primary"
							startIcon={<Add />}
							label="Neue Preisliste Fremdachse"
							onClick={() => history.push(`${basePath}/lists/new?axle=Foreign`)}
						/>
						<SidebarButton
							startIcon={<Add />}
							label="Neue Preisliste Eigenachse"
							onClick={() => history.push(`${basePath}/lists/new?axle=Own`)}
						/>
					</SidebarGroup>
					<SidebarGroup>
						<SidebarButton
							startIcon={<Description />}
							label="Excel-Vorlage"
							onClick={() => downloadFile("/api/transport-prices/template", { carrierId })}
						/>
						<SidebarButton
							startIcon={<Upload />}
							label="Excel hochladen"
							onClick={() => fileInput.current?.click()}
						/>
						<SidebarButton
							startIcon={<GetApp />}
							label="Excel-Export (heute gültig)"
							disabled={!hasActive}
							onClick={() => downloadFile("/api/transport-prices/export", { carrierId, date: today })}
						/>
					</SidebarGroup>
				</Sidebar>
			)}
		>
			<input
				ref={fileInput}
				type="file"
				accept=".xlsx"
				hidden
				onChange={async e => {
					const file = e.target.files?.[0];
					e.target.value = "";
					if (file) {
						await upload(file);
					}
				}}
			/>
			<Box mt={2}>
				{!lists && <CircularProgress size={48} />}
				{lists && lists.length > 0 && (
					<PaperTable>
						<TableHead>
							<TableRow>
								<TableCell>Achse</TableCell>
								<TableCell>Gültig</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Pauschale</TableCell>
								<TableCell>Dieselzuschlag</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{lists.map(l => {
								const status = getListStatus(l, today);

								return (
									<TableRow key={l.id} hover onClick={() => history.push(`${basePath}/lists/${l.id}`)}>
										<TableCell>{axleLabels[l.axle]}</TableCell>
										<TableCell>
											{moment(l.validFrom).format(dateFormats.date)}
											{" – "}
											{l.validTo ? moment(l.validTo).format(dateFormats.date) : "offen"}
										</TableCell>
										<TableCell><Chip size="small" color={statusColors[status]} label={statusLabels[status]} /></TableCell>
										<TableCell align="right">{l.flatFee != null ? `${l.flatFee.toFixed(2)} €` : "–"}</TableCell>
										<TableCell>{l.dieselSurchargeApplies ? "Ja" : "Nein"}</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</PaperTable>
				)}
				{lists?.length === 0 && (
					<Typography mt={2}>Für diesen Frachtführer sind noch keine Frachtpreise angelegt. Legen Sie eine Preisliste an oder laden Sie eine Excel-Datei hoch.</Typography>
				)}
			</Box>
		</Layout>
	);
};

export default TransportPriceCarrierPage;
